/**
 * The page's side of a browser cell's tool calls (code mode, CM3): a repl cell's `__steddy_call`
 * goes to the server as `sandbox.tool_call` (`sandbox.wait` for tools.wait) over this tab's agent
 * socket, under the repl call the cell runs in, and the matching `sandbox.tool_result` comes back
 * as the call's envelope. The browser's twin of the hosted pool's `host_calls.serve` (backend
 * sandbox/hosted/host_calls.py), with its words.
 *
 * Which call: the one the workspace's executor runs now (sandboxHost `serving`), as the hosted
 * pool's slot does; a host runs one call at a time per executor, and the executor refuses a cell's
 * call once the repl that made it has ended. The runtime's ticket is not needed for it.
 *
 * - Sent again under its seq whenever its answer may be lost: CM3 gives a repeated (call, seq) its
 *   first answer and never runs the tool twice, and a resend from this tab while the first is
 *   still queued is that same item. So a send that found the socket down or not yet greeted
 *   (hello_required), or one acked before the socket dropped (a frame sent while it was down is
 *   gone), goes again once it is back. Every send carries the first one's args unchanged: CM3
 *   journals a call by its args (a wait's timeout_ms too), and other args under a used seq get
 *   seq_reused, not the first answer. A timed wait sent again while queued is that item too: its
 *   `until` (its submit time plus timeout_ms) is the one its first queueing fixed
 *   (code_calls._until_key), so it looks no longer than the first; once that has passed, a resend
 *   is answered at once with the choices as they stand. One queued before the server kept that
 *   until, or whose kept until has expired, is another item, waiting its timeout_ms again from the
 *   resend. Nothing runs twice, and the journal answers a decided wait once, with its first answer.
 * - A retryable `unavailable` (a full queue, Redis) goes again after RETRY_MS, and so does
 *   `not_found` NO_CALL_MESSAGE: CM3 takes a call up only once it is paused, and the hosted pool
 *   retries it while the call may still pause (host_calls.may_go_live); the page cannot ask, so it
 *   retries until the deadline or the signal. Any other refusal is the call's answer at once, as
 *   `not_available` "The tool call could not be made: <the server's words>": code mode off, a tab
 *   that no longer holds the workspace (forbidden).
 * - The call ends with its cell (the signal: cancelled, timed out, its executor gone) or the
 *   bridge (dispose): it rejects CANCELLED, nothing is sent again, and an answer that comes later
 *   is dropped. No answer by the repl call's deadline answers it as one that could not be made:
 *   in the last transient refusal's words while no send was acked (as host_calls._submit), and
 *   NO_ANSWER_MESSAGE once one was. Its tool may have run then, so no refusal of a later resend
 *   (NO_CALL once the call went on, forbidden once the lease moved) is its answer. A send whose
 *   ack was lost in a drop does not count: a later NO_CALL then reads as never acked, which
 *   happens only at the repl call's own end, where the answer goes nowhere.
 */
import { SandboxError } from "../core/errors";
import {
  callProblem,
  refusal,
  toolCallCommand,
  toolResultEnvelope,
  type HostCall,
  type ToolResultAnswer,
} from "../core/hostCalls";
import type { HostCallHandler } from "../core/types";
import type { CommandError, SandboxToolCall } from "../../socket/client";
import type { ServedCall } from "./sandboxHost";

/** backend hosted/host_calls.RETRY_SECONDS; the last one repeats. */
const RETRY_MS = [250, 500, 1_000, 2_000, 4_000];
/** backend hosted/host_calls.NO_ANSWER_MESSAGE. */
const NO_ANSWER_MESSAGE = "No answer came before the call's time ran out.";
const NOT_SERVING_MESSAGE = "Its sandbox call no longer runs in this tab.";
/** backend sandbox/code_calls.NO_CALL: the frame's only mark of it (its code is not_found). */
const NO_CALL_MESSAGE = "No such sandbox call is running.";

export type ToolResultFrame = ToolResultAnswer & { call: string; seq: number };

/** The agent socket, as the bridge uses it (socket/client.ts). */
export interface ToolCallSocket {
  /** Resolves on the ack; rejects a CommandError, `disconnected` when it is down. */
  sendToolCall(command: SandboxToolCall): Promise<unknown>;
  onToolResult(listener: (frame: ToolResultFrame) => void): () => void;
  onReconnected(listener: () => void): () => void;
}

export interface ToolCallBridgeDeps {
  socket: ToolCallSocket;
  timers: {
    now(): number;
    setTimeout(callback: () => void, ms: number): unknown;
    clearTimeout(handle: unknown): void;
  };
}

/** The same words as host_calls.could_not. */
function couldNot(message: string): string {
  return refusal("not_available", `The tool call could not be made: ${message}`, "error");
}

const answerKey = (call: string, seq: number) => `${call}:${seq}`;

/** A refusal that goes again: CM3 may take the call a moment later. */
const transient = (error: CommandError) =>
  (error.code === "unavailable" && error.retryable === true) ||
  (error.code === "not_found" && error.message === NO_CALL_MESSAGE);

export function createToolCallBridge(deps: ToolCallBridgeDeps) {
  const { socket, timers } = deps;
  /** The calls out, by (call, seq): each takes its answer. */
  const waiting = new Map<string, (answer: ToolResultAnswer) => void>();
  /** Each call out sends again when the socket is back. */
  const resends = new Set<() => void>();
  /** Each call out, ended by dispose. */
  const ends = new Set<() => void>();
  const unlisten = socket.onToolResult((frame) =>
    waiting.get(answerKey(frame.call, frame.seq))?.(frame)
  );
  const unreconnect = socket.onReconnected(() => [...resends].forEach((resend) => resend()));
  let disposed = false;

  function serve(served: ServedCall, call: HostCall, signal: AbortSignal): Promise<string> {
    const command = toolCallCommand(served.id, call);
    // The runtime refuses these before they take a seq: unreachable from it.
    if (!command)
      return Promise.resolve(refusal("invalid", callProblem(call.name, call.argsJson)!, "invalid"));
    const key = answerKey(served.id, call.seq);
    return new Promise<string>((resolve, reject) => {
      let done = false;
      let tries = 0;
      let retry: unknown = null;
      /** Acked by some send: the server had it, and its tool may have run. An ack lost in a drop
       * leaves it false (only at the repl call's end, where the answer goes nowhere). */
      let queued = false;
      /** The last transient refusal's words while no send was acked: the deadline's answer. */
      let refused: string | null = null;
      const settle = (outcome: () => void) => {
        if (done) return;
        done = true;
        timers.clearTimeout(retry);
        timers.clearTimeout(deadline);
        if (waiting.get(key) === answered) waiting.delete(key);
        resends.delete(resend);
        ends.delete(cancel);
        signal.removeEventListener("abort", cancel);
        outcome();
      };
      const answer = (envelope: string) => settle(() => resolve(envelope));
      const answered = (frame: ToolResultAnswer) =>
        answer(JSON.stringify(toolResultEnvelope(frame)));
      const cancel = () =>
        settle(() =>
          reject(new SandboxError("CANCELLED", "CANCELLED: the cell has no use for the answer"))
        );
      const send = () => {
        retry = null;
        socket.sendToolCall(command).then(
          () => {
            queued = true; // the answer comes as a frame
            refused = null;
            // An earlier send's refusal may have set a retry: nothing goes again but on a reconnect.
            timers.clearTimeout(retry);
            retry = null;
          },
          (error: CommandError) => {
            // Sent again once the socket is back (and greeted).
            if (done || error.code === "disconnected" || error.code === "hello_required") return;
            // Once queued, a resend only looks for a lost answer: the deadline answers the rest.
            if (queued) return;
            if (!transient(error)) return answer(couldNot(error.message));
            refused = error.message;
            timers.clearTimeout(retry);
            retry = timers.setTimeout(send, RETRY_MS[Math.min(tries++, RETRY_MS.length - 1)]);
          }
        );
      };
      function resend() {
        timers.clearTimeout(retry);
        send();
      }
      const deadline =
        served.deadline === null
          ? null
          : timers.setTimeout(
              () => answer(couldNot(refused ?? NO_ANSWER_MESSAGE)),
              Math.max(0, served.deadline - timers.now())
            );
      waiting.set(key, answered);
      resends.add(resend);
      ends.add(cancel);
      signal.addEventListener("abort", cancel, { once: true });
      send();
    });
  }

  return {
    /** The host call handler of an executor; `serving` is sandboxHost's view of its call. */
    handler(serving: () => ServedCall | null): HostCallHandler {
      return async (call, signal) => {
        if (signal.aborted || disposed)
          throw new SandboxError("CANCELLED", "CANCELLED: the cell has no use for the answer");
        const served = serving();
        if (!served) return couldNot(NOT_SERVING_MESSAGE);
        return serve(served, call, signal);
      };
    },

    /** Stops listening; every call still out is cancelled, and every later one. */
    dispose() {
      disposed = true;
      unlisten();
      unreconnect();
      [...ends].forEach((end) => end());
    },
  };
}

export type ToolCallBridge = ReturnType<typeof createToolCallBridge>;
